import React, { useState } from 'react';
import { css } from 'aphrodite';
import { useFolderPickerColumns } from './FolderPickerColumns';
import { FolderData } from './types';
import styles from './styles';
import { useFolderHierarchy } from './hooks/useFolderHierarchy';
import ViewMoreRow from './ViewMoreRow';

export type FolderRowProps = {
  folder: FolderData;
  depth: number;
  selectedId: number | null;
  onRowClick: (rowData: FolderData) => void;
};

/**
 * Self-contained FolderRow component with hook-per-row pattern
 * Each row manages its own state and children independently
 * Achieves true minimal re-renders - only this row updates when expanded
 */
const FolderRow = ({
  folder,
  depth,
  selectedId,
  onRowClick,
}: FolderRowProps) => {
  // Each folder row is completely self-contained with its own hook
  const {
    children,
    isLoading,
    hasLoadedChildren,
    hasMoreChildren,
    hasLoadingError,
    loadChildren,
  } = useFolderHierarchy(folder);
  const [isExpanded, setIsExpanded] = useState(folder.expanded || false);

  const handleExpandToggle = async () => {
    setIsExpanded((prev) => !prev);

    // Load children if expanding for the first time
    if (!isExpanded && !hasLoadedChildren) {
      await loadChildren(depth + 1);
    }
  };

  const columns = useFolderPickerColumns(handleExpandToggle);

  const isSelected = selectedId === folder.id;

  const handleRowClick = () => {
    onRowClick(folder);
  };

  // Create folder data with current expand state for column rendering
  const folderColumnData: FolderData = {
    ...folder,
    expanded: isExpanded,
    level: depth, // Use current depth for proper indentation
    isSelected,
  };

  return (
    <>
      {/* Render the folder row */}
      <div
        key={folder.id}
        className={css(styles.tableRow, isSelected && styles.tableRowSelected)}
        style={{
          paddingLeft: `${20 + depth * 24}px`, // Base padding + level indentation
          paddingRight: '20px',
        }}
        onClick={handleRowClick}
        role="row"
        tabIndex={0}
        aria-selected={isSelected}
        aria-label={`Select ${folder.name} folder`}
      >
        {columns.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className="flex items-center"
            style={column.style}
          >
            {column.render
              ? column.render(
                  folderColumnData[column.dataKey as keyof FolderData] ?? null,
                  { rowData: folderColumnData },
                )
              : folderColumnData[column.dataKey as keyof FolderData]}
          </div>
        ))}
      </div>

      {/* Show loading state */}
      {isExpanded && (isLoading || hasLoadingError) && (
        <div
          style={{ paddingLeft: `${20 + depth * 24}px` }}
          className={css(styles.subFolderLoadingIndicator)}
        >
          {isLoading ? 'Loading...' : 'Error loading folders'}
        </div>
      )}

      {/* Recursively render children - each child gets its own hook instance */}
      {isExpanded && !isLoading && !hasLoadingError && (
        <>
          {children.map((childFolder) => (
            <FolderRow
              key={childFolder.id}
              folder={childFolder}
              depth={depth + 1}
              selectedId={selectedId}
              onRowClick={onRowClick}
            />
          ))}

          {/* Show "View more subfolders" row if there are more children to load */}
          {hasMoreChildren && (
            <ViewMoreRow
              depth={depth + 1}
              onLoadMore={() => loadChildren(depth + 1)}
              isLoading={isLoading}
            />
          )}
        </>
      )}
    </>
  );
};

export default FolderRow;
